import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import Layout from "../../components/global/layout";

// ─── Main Component ───────────────────────────────────────────────
const ContactUsList = () => {
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "read" | "unread"
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // ── Fetch listing with search + status params ──
  const fetchContacts = async (search = searchQuery, status = statusFilter) => {
    loader(true);
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (status) params.append("status", status);

    await ApiClient.get(`contactUs/listing?${params.toString()}`)
      .then((res) => {
        if (res?.success) setContacts(res?.data?.data || res?.data || []);
      })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // ── Debounced search ──
  useEffect(() => {
    const timer = setTimeout(
      () => fetchContacts(searchQuery, statusFilter),
      400,
    );
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── Status tab change ──
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    fetchContacts(searchQuery, status);
  };

  // ── Open view modal + fetch detail + auto-mark as read ──
  const openViewModal = async (id) => {
    loader(true);
    await ApiClient.get(`contactUs/detail?id=${id}`)
      .then(async (res) => {
        if (res?.success) {
          const contactData = res.data;
          setSelectedContact(contactData);
          setIsViewModalOpen(true);

          // Auto-update to read status if it was unread
          if (contactData.status !== "read") {
            await ApiClient.put(`contactUs/status/change?id=${id}&status=read`)
              .then((statusRes) => {
                if (statusRes?.success) {
                  setSelectedContact((prev) =>
                    prev ? { ...prev, status: "read" } : prev,
                  );
                  setContacts((prev) =>
                    prev.map((c) =>
                      (c.id || c._id) === id ? { ...c, status: "read" } : c,
                    ),
                  );
                }
              })
              .catch(console.error);
          }
        }
      })
      .catch(console.error);
    loader(false);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedContact(null);
  };

  // ── Toggle read/unread from table row ──
  const handleToggleStatus = async (item) => {
    const id = item?.id || item?._id;
    const newStatus = item.status === "read" ? "unread" : "read";
    loader(true);
    await ApiClient.put(`contactUs/status/change?id=${id}&status=${newStatus}`)
      .then((res) => {
        if (res?.success) {
          setContacts((prev) =>
            prev.map((c) =>
              (c.id || c._id) === id ? { ...c, status: newStatus } : c,
            ),
          );
        }
      })
      .catch(console.error);
    loader(false);
  };

  // ── Toggle status from modal ──
  const handleModalToggleStatus = async () => {
    const id = selectedContact?.id || selectedContact?._id;
    const newStatus = selectedContact.status === "read" ? "unread" : "read";
    loader(true);
    await ApiClient.put(`contactUs/status/change?id=${id}&status=${newStatus}`)
      .then((res) => {
        if (res?.success) {
          setSelectedContact((prev) => ({ ...prev, status: newStatus }));
          setContacts((prev) =>
            prev.map((c) =>
              (c.id || c._id) === id ? { ...c, status: newStatus } : c,
            ),
          );
        }
      })
      .catch(console.error);
    loader(false);
  };

  // ── Delete ──
  const handleDelete = (id) => {
    swal({
      text: "This contact message will be permanently deleted.",
      icon: "warning",
      className: "smallModal",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      await ApiClient.delete(`contactUs/delete?id=${id}`)
        .then(async (res) => {
          if (res?.success) {
            await fetchContacts();
            swal({
              text: "Contact message deleted successfully.",
              icon: "success",
              className: "smallModal",
            });
          }
        })
        .catch(console.error);
      loader(false);
    });
  };

  // ── Format date ──
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ── Format time ──
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ── Avatar initials ──
  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // ── Status badge ──
  const StatusBadge = ({ status }) => {
    const isRead = status === "read";
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
          isRead
            ? "bg-green/20 text-green border border-[#116b2552]"
            : "bg-amber-50 text-amber-700 border border-amber-200"
        }`}
      >
        <span
          className={`size-1.5 rounded-full ${isRead ? "bg-[#0a7822a6]" : "bg-amber-500"}`}
        />
        {isRead ? "Read" : "Unread"}
      </span>
    );
  };

  const STATUS_TABS = [
    { label: "All", value: "" },
    { label: "Unread", value: "unread" },
    { label: "Read", value: "read" },
  ];

  const unreadCount = contacts.filter((c) => c.status === "unread").length;

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
        {/* ─── Page Header ─── */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
              Contact Us
            </h2>
            <p className="text-sm sm:text-md text-gray-600">
              {contacts.length} message{contacts.length !== 1 ? "s" : ""} total
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-xs font-semibold">
                  {unreadCount} unread
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          {/* ─── Filters Row ─── */}
          <div className="flex justify-between flex-wrap gap-3 mb-4">
            {/* Status Tabs */}
            <div className="max-sm:w-full overflow-auto w-full sm:w-fit overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl p-1.5">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => handleStatusFilter(tab.value)}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    statusFilter === tab.value
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-[350px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
              />
            </div>
          </div>

          {/* ─── Table ─── */}

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden border border-gray-200 rounded-xl">
            <table className="w-full table-fixed">
              <colgroup>
                <col style={{ width: "13%" }} /> {/* Actions */}
                <col style={{ width: "16%" }} /> {/* Customer */}
                <col style={{ width: "19%" }} /> {/* Email */}
                <col style={{ width: "22%" }} /> {/* Message */}
                <col style={{ width: "12%" }} /> {/* Status */}
                <col style={{ width: "18%" }} /> {/* Received */}
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "Actions",
                    "Customer",
                    "Email",
                    "Message",
                    "Status",
                    "Received",
                  ].map((h) => (
                    <th key={h} className="text-left py-3 px-3">
                      <span className="text-xs text-gray-700 font-medium truncate block">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-600"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">
                          No contact messages found
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  contacts.map((item, index) => {
                    const isUnread = item.status === "unread";
                    return (
                      <tr
                        key={item.id || item._id || index}
                        className={`hover:bg-gray-50/60 transition-colors align-middle ${isUnread ? "bg-amber-50/30" : ""}`}
                      >
                        {/* Actions */}
                        <td className="py-2.5 px-3 align-middle">
                          <div className="flex items-center gap-1">
                            {/* View */}
                            <button
                              type="button"
                              onClick={() =>
                                openViewModal(item?.id || item?._id)
                              }
                              title="View"
                              className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>

                            {/* Toggle Read / Unread */}
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(item)}
                              title={
                                isUnread ? "Mark as Read" : "Mark as Unread"
                              }
                              className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-amber-500 transition-colors shrink-0"
                            >
                              {isUnread ? (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <rect
                                    width="20"
                                    height="16"
                                    x="2"
                                    y="4"
                                    rx="2"
                                  />
                                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                                </svg>
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M22 10.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12.5" />
                                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                                  <circle cx="19" cy="19" r="3" />
                                </svg>
                              )}
                            </button>

                            {/* Delete */}
                            <button
                              type="button"
                              onClick={() =>
                                handleDelete(item?.id || item?._id)
                              }
                              title="Delete"
                              className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors shrink-0"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 6h18" />
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                <line x1="10" x2="10" y1="11" y2="17" />
                                <line x1="14" x2="14" y1="11" y2="17" />
                              </svg>
                            </button>
                          </div>
                        </td>

                        {/* Customer */}
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="size-8 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                              <span className="text-[10px] font-semibold text-[#115B9D]">
                                {getInitials(item.fullName)}
                              </span>
                            </div>
                            <p
                              className={`text-xs break-words capitalize leading-tight ${isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-700"}`}
                            >
                              {item.fullName || "—"}
                            </p>
                          </div>
                        </td>

                        {/* Email — full show, no truncate */}
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <p
                            className="text-xs text-gray-700 break-all leading-tight"
                            title={item.email}
                          >
                            {item.email || "—"}
                          </p>
                        </td>

                        {/* Message — clamped to 2 lines, full text via View button */}
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <p
                            className="text-xs text-gray-500 leading-snug cursor-pointer"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                            title={item.message}
                          >
                            {item.message || "—"}
                          </p>
                        </td>

                        {/* Status */}
                        <td className="py-2.5 px-3 align-middle">
                          <StatusBadge status={item.status} />
                        </td>

                        {/* Received Date */}
                        <td className="py-2.5 px-3 align-middle">
                          <p className="text-xs text-gray-700 whitespace-nowrap">
                            {formatDate(item.createdAt)}
                          </p>
                          <p className="text-[11px] text-gray-600">
                            {formatTime(item.createdAt)}
                          </p>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {contacts.length === 0 ? (
              <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-600"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500">
                  No contact messages found
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {contacts.map((item, index) => {
                  const isUnread = item.status === "unread";
                  return (
                    <div
                      key={item.id || item._id || index}
                      className={`border border-gray-200 rounded-xl p-4 bg-white ${isUnread ? "bg-amber-50/30" : ""}`}
                    >
                      {/* Top row: Customer + Actions */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-start gap-2.5 min-w-0">
                          <div className="size-9 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-[#115B9D]">
                              {getInitials(item.fullName)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p
                              className={`text-sm break-words capitalize ${isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-700"}`}
                            >
                              {item.fullName || "—"}
                            </p>
                            <p className="text-xs text-gray-600">
                              {formatDate(item.createdAt)} ·{" "}
                              {formatTime(item.createdAt)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => openViewModal(item?.id || item?._id)}
                            title="View"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleToggleStatus(item)}
                            title={isUnread ? "Mark as Read" : "Mark as Unread"}
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-amber-500 transition-colors"
                          >
                            {isUnread ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  width="20"
                                  height="16"
                                  x="2"
                                  y="4"
                                  rx="2"
                                />
                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M22 10.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12.5" />
                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                                <circle cx="19" cy="19" r="3" />
                              </svg>
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(item?.id || item?._id)}
                            title="Delete"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                              <line x1="10" x2="10" y1="11" y2="17" />
                              <line x1="14" x2="14" y1="11" y2="17" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex justify-between items-start py-1.5 border-t border-gray-100">
                        <span className="text-xs text-gray-500 shrink-0 font-extrabold">
                          Email
                        </span>
                        <p className="text-sm text-gray-700 text-right break-all ml-3">
                          {item.email || "—"}
                        </p>
                      </div>

                      {/* Message */}
                      <div>
                        <div className="flex justify-between items-start py-1.5 border-t border-gray-100 truncate">
                          <span className="text-xs text-gray-500 shrink-0 font-extrabold">
                            Message :
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 text-right break-words truncate">
                          {item.message || "—"}
                        </p>
                      </div>

                      {/* Status */}
                      <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                        <span className="text-xs text-gray-500 font-extrabold">
                          Status
                        </span>
                        <StatusBadge status={item.status} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── View Contact Modal ─── */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={closeViewModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="min-w-9 w-9 h-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-[#115B9D]"
                        >
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Contact Message Details
                        </Dialog.Title>
                        {selectedContact?.status && (
                          <div className="mt-1">
                            <StatusBadge status={selectedContact.status} />
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={closeViewModal}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Body */}
                  {selectedContact && (
                    <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5 scrollbar">
                      {/* ── Avatar + Name ── */}
                      <div className="flex items-center gap-4 p-4 bg-[#115B9D08] border border-[#115B9D]/10 rounded-xl">
                        <div className="size-14 rounded-xl bg-[#115B9D17] border border-[#115B9D]/15 flex items-center justify-center shrink-0">
                          <span className="text-lg font-bold text-[#115B9D]">
                            {getInitials(selectedContact.fullName)}
                          </span>
                        </div>
                        <div>
                          <p className="text-base font-semibold text-gray-900 capitalize">
                            {selectedContact.fullName || "—"}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            Submitted on {formatDate(selectedContact.createdAt)}{" "}
                            {formatTime(selectedContact.createdAt) &&
                              `at ${formatTime(selectedContact.createdAt)}`}
                          </p>
                        </div>
                      </div>

                      {/* ── Contact Info ── */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Contact Information
                        </p>
                        <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {/* Email */}
                          <div className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                            <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-[#115B9D]"
                              >
                                <rect
                                  width="20"
                                  height="16"
                                  x="2"
                                  y="4"
                                  rx="2"
                                />
                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                              </svg>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">
                                Email
                              </p>
                              <p className="text-sm text-gray-800 break-all mt-0.5">
                                {selectedContact.email || "—"}
                              </p>
                            </div>
                          </div>

                          {/* Phone */}
                          <div className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                            <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-[#115B9D]"
                              >
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                              </svg>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">
                                Phone
                              </p>
                              <p className="text-sm text-gray-800 mt-0.5 break-words">
                                {selectedContact.mobileNo || "—"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ── Message ── */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Message
                        </p>
                        <div className="pt-2">
                          <div className="p-3 border border-gray-100 rounded-xl bg-gray-50/50 min-h-[80px]">
                            {selectedContact.message ? (
                              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                {selectedContact.message}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600 italic">
                                No message provided.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Modal Footer */}
                  <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
                    {/* Mark Read / Unread */}
                    {selectedContact && (
                      <button
                        type="button"
                        onClick={handleModalToggleStatus}
                        className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect width="20" height="16" x="2" y="4" rx="2" />
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                        </svg>
                        Mark as{" "}
                        {selectedContact?.status === "read" ? "Unread" : "Read"}
                      </button>
                    )}

                    <div className="flex flex-wrap items-center gap-3 ml-auto">
                      <button
                        type="button"
                        onClick={closeViewModal}
                        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
                      >
                        Close
                      </button>
                      {selectedContact && (
                        <button
                          type="button"
                          onClick={() => {
                            closeViewModal();
                            handleDelete(
                              selectedContact?.id || selectedContact?._id,
                            );
                          }}
                          className="inline-flex items-center gap-2 bg-red-500 hover:opacity-80 text-white text-sm px-4 py-2.5 rounded-xl transition-colors font-medium"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 6h18" />
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                          </svg>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
};

export default ContactUsList;
